import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';
import { LotteryOutput, lotterySpecialLabel } from '@/lib/lottery';

type LotteryBallsProps = {
  output: LotteryOutput;
  preset?: string | null;
};

function Ball({
  value,
  variant = 'main',
}: {
  value: number;
  variant?: 'main' | 'powerball' | 'megaball';
}) {
  return (
    <span
      className={clsxm(
        // 36 px on phones, so a Powerball ticket stays on one line.
        'inline-flex h-9 w-9 select-none items-center justify-center rounded-full text-[15px] font-bold shadow-md ring-1 sm:h-12 sm:w-12 sm:text-lg',
        variant === 'powerball' &&
          'bg-gradient-to-b from-red-500 to-red-600 text-white ring-red-700/30',
        variant === 'megaball' &&
          'bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 ring-amber-600/40',
        variant === 'main' &&
          'bg-gradient-to-b from-white to-gray-100 text-gray-800 ring-gray-300 dark:from-slate-100 dark:to-slate-200 dark:text-gray-900',
      )}
    >
      {value}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <span className='inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700'>
      {label}
      <span className='font-semibold tabular-nums text-gray-800 dark:text-slate-200'>
        {value}
      </span>
    </span>
  );
}

export default function LotteryBalls({ output, preset }: LotteryBallsProps) {
  const { t } = useLocale('common');
  const { sets } = output;
  if (!sets || sets.length === 0) return null;

  const specialLabel = output.bonusLabel || lotterySpecialLabel(preset);
  const specialVariant =
    specialLabel === 'Mega Ball' ||
    (!output.bonusLabel && preset === 'mega-millions-number-generator')
      ? 'megaball'
      : 'powerball';
  const hasStats = sets.some((set) => set.stats);

  return (
    <div className='space-y-4'>
      {output.game && output.game !== 'Custom' ? (
        <p className='text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400'>
          {output.game}
        </p>
      ) : null}
      {output.notices.map((notice) => (
        <p
          key={notice}
          className='rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-400/10 dark:text-amber-200 dark:ring-amber-400/20'
        >
          {notice === 'rules_ignored'
            ? t(
                'We couldn’t read your custom rules, so these numbers follow the game’s usual rules.',
              )
            : t(
                'Leaving out those numbers would leave too few to choose from, so none were left out.',
              )}
        </p>
      ))}
      {sets.map((set, i) => (
        <div
          key={i}
          className='rounded-xl border border-gray-100 bg-gray-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40'
        >
          <div className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500'>
            {sets.length > 1
              ? t('Set {{number}}', { number: i + 1 })
              : t('Your numbers')}
          </div>
          <div
            className={clsxm(
              'flex flex-wrap items-center gap-x-1.5 gap-y-1.5 sm:gap-x-2',
              // Room for the bonus balls' captions, which sit outside the
              // flow: below the row, and between rows when a ball wraps.
              set.specials?.length && specialLabel && 'gap-y-6 pb-5',
            )}
          >
            {set.main.map((n, j) => (
              <Ball key={j} value={n} />
            ))}
            {set.specials && set.specials.length > 0 && (
              <>
                <span
                  aria-hidden
                  className='mx-0.5 text-xl font-light text-gray-300 dark:text-slate-600'
                >
                  +
                </span>
                {set.specials.map((special, j) => (
                  <span key={j} className='relative inline-flex'>
                    <Ball value={special} variant={specialVariant} />
                    {/* Out of the flow: a caption wider than its ball
                        would push the ball onto its own line on phones. */}
                    {specialLabel && (
                      <span className='absolute inset-x-0 top-full mt-1 flex justify-center'>
                        <span className='whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500'>
                          {specialLabel}
                        </span>
                      </span>
                    )}
                  </span>
                ))}
              </>
            )}
          </div>
          {set.stats && (
            <div className='mt-3 flex flex-wrap gap-1.5'>
              <Stat
                label={t('Odd/even')}
                value={`${set.stats.odd}/${set.stats.even}`}
              />
              <Stat
                label={t('Low/high')}
                value={`${set.stats.low}/${set.stats.high}`}
              />
              <Stat label={t('Sum')} value={set.stats.sum} />
            </div>
          )}
          {set.analysis && (
            <p className='mt-3 text-sm text-gray-600 dark:text-slate-400'>
              {set.analysis}
            </p>
          )}
        </div>
      ))}
      {hasStats ? (
        <p className='text-xs leading-5 text-gray-500 dark:text-slate-400'>
          {t(
            'Every combination has the same chance to win. Odd/even, low/high (lower or upper half of the range) and the sum only describe your numbers.',
          )}
        </p>
      ) : null}
    </div>
  );
}
